// QpayCenterTransactionReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { formatDateTimeIST } from '../../utils/dateFormat';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const formatToIST = (dateStr) => formatDateTimeIST(dateStr);

const QpayCenterTransactionReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');

  // Dropdown options
  const [allStatesList, setAllStatesList] = useState(['Kerala']);
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [rawTaluksList, setRawTaluksList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);
  const [qpayCentersList, setQpayCentersList] = useState([]);

  // Filter States
  const [stateFilter, setStateFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' or 'merchant'
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  const token = sessionStorage.getItem('token');

  // Decode token
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, [token]);

  // Fetch dropdown lists
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const stateRes = await axios.get(`${API_BASE_URL}/states`);
        if (stateRes.status === 200 && stateRes.data) {
          const list = Array.isArray(stateRes.data)
            ? stateRes.data
            : stateRes.data.states || [];
          const sNames = list.map((s) => s.stateName || s.name).filter(Boolean);
          if (sNames.length > 0) setAllStatesList(sNames);
        }
      } catch (err) {
        console.error('Error fetching states dropdown:', err);
      }

      try {
        const distRes = await axios.get(`${API_BASE_URL}/districts`);
        if (distRes.status === 200 && distRes.data) {
          const list = Array.isArray(distRes.data)
            ? distRes.data
            : distRes.data.districts || [];
          setAllDistrictsList(list);
        }
      } catch (err) {
        console.error('Error fetching districts dropdown:', err);
      }

      try {
        const talukRes = await axios.get(`${API_BASE_URL}/taluks`);
        if (talukRes.status === 200 && talukRes.data) {
          const list = Array.isArray(talukRes.data)
            ? talukRes.data
            : talukRes.data.taluks || [];
          setRawTaluksList(list);
          const names = [...new Set(list.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
          setAllConstituenciesList(names.sort());
        }
      } catch (err) {
        console.error('Error fetching constituencies dropdown:', err);
      }

      try {
        const qcRes = await axios.post(`${API_BASE_URL}/qpay-centers/all`, { userId });
        if (qcRes.status === 200 && qcRes.data && qcRes.data.data) {
          setQpayCentersList(qcRes.data.data);
        }
      } catch (err) {
        console.error('Error fetching Q-Pay Centers dropdown:', err);
      }
    };

    fetchDropdownData();
  }, [userId]);

  // Update Constituency dropdown whenever District filter changes
  useEffect(() => {
    setVillageFilter('all');
    if (districtFilter === 'all') {
      const names = [...new Set(rawTaluksList.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    } else {
      const selectedDistObj = allDistrictsList.find(
        (d) => (d.districtName || d.name || '').toLowerCase() === districtFilter.toLowerCase()
      );
      const selectedDistId = selectedDistObj ? String(selectedDistObj._id || selectedDistObj.id) : null;

      const filteredTaluks = rawTaluksList.filter((t) => {
        const tDistId = String(t.district || t.districtId || '');
        const tDistName = (t.districtName || '').toLowerCase();
        return (
          (selectedDistId && tDistId === selectedDistId) ||
          tDistName === districtFilter.toLowerCase()
        );
      });

      const names = [...new Set(filteredTaluks.map((t) => t.talukName || t.name || t.taluk).filter(Boolean))];
      setAllConstituenciesList(names.sort());
    }
  }, [districtFilter, allDistrictsList, rawTaluksList]);

  // Fetch Transaction Report
  const fetchTransactionsReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        userId,
        startDate,
        endDate,
        state: stateFilter,
        district: districtFilter,
        village: villageFilter,
        qpayCenterId: qpayCenterFilter,
        filterType: typeFilter,
        search: searchQuery,
        sortOrder,
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };

      const response = await axios.post(
        `${API_BASE_URL}/qpay-centers/transactions-report`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        // [CASH-MODE] client-side guard in case the API ignores the filter
        setReportData(filterRowsByMode(response.data.data || [], paymentMode));
        setTotalsByMode(response.data.totalsByMode || null);
      } else {
        setError('Failed to fetch transaction report.');
      }
    } catch (err) {
      console.error('Error fetching transaction report:', err);
      setError('An error occurred while fetching the transaction report.');
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    startDate,
    endDate,
    stateFilter,
    districtFilter,
    villageFilter,
    qpayCenterFilter,
    typeFilter,
    searchQuery,
    sortOrder,
    token,
    paymentMode // [CASH-MODE]
  ]);

  useEffect(() => {
    if (userId) {
      fetchTransactionsReport();
    }
  }, [userId, fetchTransactionsReport]);

  const handleResetFilters = () => {
    setStateFilter('all');
    setDistrictFilter('all');
    setVillageFilter('all');
    setQpayCenterFilter('all');
    setTypeFilter('all');
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setSortOrder('desc');
    setPaymentMode(PAYMENT_MODES.ALL); // [CASH-MODE]
  };

  // CSV Export for Level 1 Super Admin ONLY
  const handleExportCSV = () => {
    if (!can('qpayCentre.transactions.export')) return;
    if (reportData.length === 0) {
      alert('No data available to export.');
      return;
    }

    const headers = [
      'SL No',
      'Date & Time',
      'Merchant ID',
      'Customer No',
      'Customer Name',
      'Customer VPA ID',
      'Type',
      'Mode', // [CASH-MODE]
      'Original Transaction Amount',
      'Q-Pay Center Commission Amount'
    ];

    const rows = reportData.map((row) => [
      row.slNo,
      `"${formatToIST(row.dateTime)}"`,
      `"${row.merchantId || 'N/A'}"`,
      `"${row.customerId || 'N/A'}"`,
      `"${row.customerName || 'N/A'}"`,
      `"${row.customerVpa || '-'}"`,
      `"${row.type || 'Credit'}"`,
      paymentModeLabel(row), // [CASH-MODE]
      row.originalAmount || 0,
      row.qpayCommissionAmount || 0
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `qpay_centre_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const modeTotals = totalsByMode || totalsByModeFromRows(reportData, (r) => r.originalAmount);
  const modeAmountKey = modeTotals?.UPI?.originalAmount !== undefined ? 'originalAmount' : 'amount';

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
          Q-PAY Centre Transaction Report
        </Typography>
      </Box>

      {/* Filters Box */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          {/* State Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>State wise</InputLabel>
              <Select
                value={stateFilter}
                label="State wise"
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <MenuItem value="all">All States</MenuItem>
                {allStatesList.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* District Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>District wise</InputLabel>
              <Select
                value={districtFilter}
                label="District wise"
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                {allDistrictsList.map((d) => {
                  const dName = d.districtName || d.name;
                  return (
                    <MenuItem key={d._id || dName} value={dName}>
                      {dName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency Wise */}
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Constituency wise</InputLabel>
              <Select
                value={villageFilter}
                label="Constituency wise"
                onChange={(e) => setVillageFilter(e.target.value)}
              >
                {allConstituenciesList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Q-Pay Centre Wise */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Q-Pay Centre wise</InputLabel>
              <Select
                value={qpayCenterFilter}
                label="Q-Pay Centre wise"
                onChange={(e) => setQpayCenterFilter(e.target.value)}
              >
                <MenuItem value="all">All Q-Pay Centres</MenuItem>
                {qpayCentersList.map((qc) => (
                  <MenuItem key={qc.id} value={qc.id}>
                    {qc.customId} - {qc.firstName} {qc.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Type: All / Merchant */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by</InputLabel>
              <Select
                value={typeFilter}
                label="Filter by"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="merchant">Merchant</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          {/* [CASH-MODE] */}
          <Grid item xs={12} sm={6} md={2}>
            <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
          </Grid>

          {/* Search Bar */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Search Merchant / Customer / VPA"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon color="action" fontSize="small" />
              }}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
              fullWidth
              size="small"
              sx={{ height: 40, borderRadius: 2 }}
            >
              Reset
            </Button>
            {can('qpayCentre.transactions.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                fullWidth
                size="small"
                sx={{ height: 40, fontWeight: 700, borderRadius: 2 }}
              >
                CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* [CASH-MODE] */}
      <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mb: 2 }} />

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  SL No
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Date & Time
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Merchant ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer No
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Customer VPA ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Type (Credit/Debit)
                </TableCell>
                {/* [CASH-MODE] */}
                <TableCell sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Mode
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Original Transaction Amount
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
                  Q-Pay Center Commission Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3, color: '#64748b' }}>
                    No transactions found for the selected criteria.
                  </TableCell>
                </TableRow>
              ) : (
                reportData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.slNo}</TableCell>
                    <TableCell>{formatToIST(row.dateTime)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2563eb' }}>{row.merchantId}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.customerId}</TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.customerVpa || '-'}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          backgroundColor: row.type === 'Credit' ? '#dcfce7' : '#fee2e2',
                          color: row.type === 'Credit' ? '#15803d' : '#b91c1c'
                        }}
                      >
                        {row.type}
                      </Box>
                    </TableCell>
                    {/* [CASH-MODE] */}
                    <TableCell>
                      <PaymentModeChip row={row} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₹{(row.originalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#059669' }}>
                      ₹{(row.qpayCommissionAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default QpayCenterTransactionReport;
